import { useEffect, useMemo, useState } from 'react';

import {
    Label,
    Textarea,
    Spinner,
    Tooltip
} from "flowbite-react";
import toast from 'react-hot-toast';
import Select from 'react-select';
import { HiQuestionMarkCircle, HiTrash, HiCode } from "react-icons/hi";
import { useSelector, useDispatch } from 'react-redux'
import { useProjectService } from "../../../../services";
import {
    resetTaskData
} from '../../../../redux/slices/projects';

import { formatDate, removeBlankKeyfromObject } from "../../../../helpers/data-format";

import { useOrganizationService, useResourcesService } from '../../../../services';
import { useJwt } from "../../../../helpers/common";
import { DeleteModal, Drawer } from "../../../../components/Modals";
import CustomButton from '../../../../components/common/CustomButton';
import RichTextEditor from '../../../../components/RichTextEditor';
import { useResourceInfo } from "../../../../hooks/useResourceInfo";
import { getDefaultUserName } from "../../../../constants/defaultUsers";
import moment from "moment";
import FormInput from '../../../../components/common/FormInput';
import CustomDropdown from '../../../../components/common/CustomDropdown';
import MultiSelect from '../../../../components/common/MultiSelect';
import NumberInput from '../../../../components/common/NumberInput';


interface TaskCompletionUpdateModalProps {
    project_id?: any, selectedTask?: any,
    showModal?: any, onClose?: any, handleAddNewTask?: any,
    isVisiable?: any, tasks?: any,
    members?: any[],
    sim_flag?: any,
    isToken?: any,
    token?: any,
    onTaskUpdate?: any
}

const TaskCompletionUpdateModal = ({ onClose, project_id, onTaskUpdate, selectedTask, isVisiable, handleAddNewTask, tasks, members, sim_flag, isToken = false }: TaskCompletionUpdateModalProps) => {
    const dispatch = useDispatch();
    const { updateTaskCompletion, createTask, fetchTask, deleteTask } = useProjectService();
    const { fetchOrganizationSkill } = useOrganizationService();
    const { getResourcesbyIds } = useResourcesService();
    const { getResourceName } = useResourceInfo();
    const JWTdata: any = useJwt();
    const org_id = JWTdata.org_id;

    const { task = {}, taskLoading, loading: projectLoading, error } = useSelector((state: any) => state.projects)
    const { skills = [], skillLoading } = useSelector((state: any) => state.organization);
    const resourcesbyIdsLoading = useSelector((state: any) => state.resources.loading);

    const [completion, setCompletion] = useState(selectedTask?.completion || 0);
    const [description, setDescription] = useState(selectedTask?.description);
    const [task_title, setTitle] = useState(selectedTask?.task_title);
    const [skill, setSkill] = useState(0);
    const [duration, setDuration] = useState(selectedTask?.duration);
    const [responsible, setResponsible] = useState(selectedTask?.responsible);
    const [start_date, setStartDate] = useState(selectedTask?.start_date);
    const [task_type, setTask_type] = useState(selectedTask?.task_type || "task");
    const [dependenciesIds, setDependenciesIds] = useState<(string | number)[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [bid_price, setBidPrice] = useState(selectedTask?.bid_price);
    const [billing_price, setBillingPrice] = useState(selectedTask?.billing_price);
    const [additional_resources, setAdditionalResources] = useState([]);
    const [delayed_duration, setDelayedDuration] = useState(selectedTask?.delayed_duration || 0);
    const [float_start_date, setFloatStartDate] = useState(selectedTask?.float_start_date);

    // Detect unsaved changes for edit mode
    const unsavedChanges = useMemo(() => {
        if (!task?.item_id || taskLoading || !task || Object.keys(task).length === 0) {
            return false;
        }

        if (!task.task_title) {
            return false;
        }

        const normalizeDescription = (desc: string) => {
            if (!desc) return '';
            try {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = desc;
                const textContent = tempDiv.textContent || tempDiv.innerText || '';
                let normalized = textContent.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');
                return normalized.trim();
            } catch (error) {
                let normalized = desc.replace(/<[^>]*>/g, '');
                normalized = normalized.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');
                return normalized.trim();
            }
        };

        const normalizeTitle = (title: string) => {
            if (!title) return '';
            return title.replace(/\s+/g, ' ').trim();
        };

        // Compare title
        const originalTitle = normalizeTitle(task.task_title || '');
        const currentTitle = normalizeTitle(task_title || '');
        if (originalTitle !== currentTitle) {
            return true;
        }

        // Compare description
        const currentDescRaw = description || '';
        const initialDescRaw = task.description || '';
        const isInitialLoad = !currentDescRaw && initialDescRaw;
        const currentDescNormalized = normalizeDescription(currentDescRaw);
        const initialDescNormalized = normalizeDescription(initialDescRaw);
        const descExactMatch = currentDescNormalized === initialDescNormalized;
        const descContentMatch = currentDescNormalized.replace(/\s/g, '') === initialDescNormalized.replace(/\s/g, '');
        const descriptionChanged = !isInitialLoad && !descExactMatch && !descContentMatch;
        if (descriptionChanged) {
            return true;
        }

        // Compare skill
        const originalSkillId = task.skill_id ? String(task.skill_id) : '';
        const currentSkillId = skill ? String(skill) : '';
        if (originalSkillId !== currentSkillId) {
            return true;
        }

        // Compare duration
        if (Number(task.duration || 0) !== Number(duration || 0)) {
            return true;
        }

        // Compare delayed_duration
        if (Number(task.delayed_duration || 0) !== Number(delayed_duration || 0)) {
            return true;
        }

        // Compare task_type
        if ((task.task_type || '') !== (task_type || '')) {
            return true;
        }

        // Compare completion
        if (Number(task.completion || 0) !== Number(completion || 0)) {
            return true;
        }

        // Compare dependencies
        const originalDeps = (task.dependency || []).map((id: any) => String(id)).sort();
        const currentDeps = (dependenciesIds || []).map((id: any) => String(id)).sort();
        if (originalDeps.length !== currentDeps.length) {
            return true;
        }
        for (let i = 0; i < originalDeps.length; i++) {
            if (originalDeps[i] !== currentDeps[i]) {
                return true;
            }
        }

        // Compare bid_price and billing_price
        if (Number(task.bid_price || 0) !== Number(bid_price || 0)) {
            return true;
        }
        if (Number(task.billing_price || 0) !== Number(billing_price || 0)) {
            return true;
        }

        // Compare float_start_date for float tasks
        const formatDateValue = (val: any) => {
            if (!val) return "";
            if (val instanceof Date) return formatDate(val.toISOString());
            return formatDate(String(val));
        };

        if (task_type === "float") {
            const originalFloatDate = task.float_start_date
                ? formatDateValue(task.float_start_date)
                : "";
            const currentFloatDate = float_start_date
                ? formatDateValue(float_start_date)
                : "";
            if (originalFloatDate !== currentFloatDate) {
                return true;
            }
        }

        // Always compare start_date
        const originalStartDate = task.start_date
            ? formatDateValue(task.start_date)
            : "";
        const currentStartDate = start_date
            ? formatDateValue(start_date)
            : "";
        if (originalStartDate !== currentStartDate) {
            return true;
        }

        return false;
    }, [
        task,
        taskLoading,
        task_title,
        description,
        skill,
        duration,
        delayed_duration,
        task_type,
        completion,
        dependenciesIds,
        bid_price,
        billing_price,
        start_date,
        float_start_date,
    ]);

    const onSubmit = async () => {
        if (!task_title) {
            toast.error("Title can not be null/empty !", { duration: 1000 });
            return
        }
        if (!description) {
            toast.error("Description can not be null/empty !", { duration: 1000 });
            return
        }
        if (!skill) {
            toast.error("Please select skill type !");
            return
        }
        if (!duration || duration < 1) {
            toast.error("Please add duration !");
            return
        }

        if (task?.item_id) {
            const UpdateTaskCompletionObject: any = {
                completion,
                skill: "",
                description,
                task_title,
                duration,
                responsible,
                dependencies: dependenciesIds,
                sim_flag,
                skill_id: Number(skill),
                bid_price: Number(bid_price),
                billing_price: Number(billing_price),
                additional_resources,
                delayed_duration: Number(delayed_duration),
            };
            if (start_date) {
                UpdateTaskCompletionObject['start_date'] = formatDate(start_date)
            }
            if (float_start_date && task_type === 'float') {
                UpdateTaskCompletionObject['float_start_date'] = formatDate(float_start_date)
            }
            const data = removeBlankKeyfromObject(UpdateTaskCompletionObject)
            data['responsible'] = responsible
            data['delayed_date'] = ""

            await updateTaskCompletion(project_id, task.item_id, data);
            if (onTaskUpdate) await onTaskUpdate();
        } else {
            const taskPayload = {
                completion,
                description,
                task_title,
                task_type,
                dependencies: dependenciesIds,
                skill: "",
                duration,
                responsible,
                sim_flag,
                skill_id: Number(skill),
                bid_price: Number(bid_price),
                billing_price: Number(billing_price),
                additional_resources,
                delayed_duration: Number(delayed_duration),
            };
            if (float_start_date && task_type === 'float') {
                taskPayload['float_start_date'] = formatDate(float_start_date)
            }
            await createTask(project_id, taskPayload);
            if (onTaskUpdate) await onTaskUpdate();
        }
        handleCloseModal();
    }
    const handleDeleteTask = (status: boolean) => {
        setShowDeleteConfirm(status)
    }
    const submitDeleteTask = async () => {
        if (Object.keys(selectedTask).length) {
            let item_id = selectedTask.id;
            if (selectedTask.item_id) {
                item_id = selectedTask.item_id
            }
            await deleteTask(project_id, item_id, sim_flag);
            if (onTaskUpdate) await onTaskUpdate();
            handleCloseModal();
        }
    }

    useEffect(() => {
        if (task && Object.keys(task) && Object.keys(selectedTask).length) {
            setCompletion(task?.completion)
            setDescription(task?.description)
            setTitle(task?.task_title)
            setTask_type(task?.task_type)
            if (task?.start_date) setStartDate(new Date(task?.start_date))
            if (task?.float_start_date) setFloatStartDate(new Date(task?.float_start_date))
            setResponsible(task?.responsible)
            setDuration(task?.duration)
            setBidPrice(task?.bid_price)
            setBillingPrice(task?.billing_price)
            setSkill(task?.skill_id)
            if (task?.dependency) {
                setDependenciesIds(task?.dependency)
            }
            if (task?.additional_resources) {
                setAdditionalResources(task?.additional_resources)
            }
            setDelayedDuration(task?.delayed_duration || 0)
        }
    }, [task, resourcesbyIdsLoading])

    useEffect(() => {
        return () => handleCloseModal();
    }, [])

    useEffect(() => {
        if (Object.keys(selectedTask).length && !Object.keys(task).length) {
            let item_id = selectedTask.id;
            if (selectedTask.item_id) {
                item_id = selectedTask.item_id
            }
            if (!taskLoading) {
                fetchTask(project_id, item_id, sim_flag);
            }

        }
    }, []);
    useEffect(() => {
        if (JWTdata.org_id) {
            fetchOrganizationSkill(JWTdata.org_id)
        }
    }, [JWTdata.org_id]);


    useEffect(() => {
        if (members && members.length && !resourcesbyIdsLoading) {
            getResourcesbyIds(project_id, { resource_id: members })
        }
    }, [projectLoading, taskLoading, resourcesbyIdsLoading])

    const handleCloseModal = () => {
        handleAddNewTask(false)
        setCompletion(0)
        setDescription('')
        setTask_type('task')
        setSkill('')
        setDuration('')
        setBidPrice('')
        setBillingPrice('')
        setStartDate('')
        setResponsible('')
        setTitle('')
        onClose({})
        dispatch(resetTaskData())
    }

    const handleSelectChange = (value) => {
        const ids = value.map(el => el.value)
        setDependenciesIds(ids)

    }

    const handleCompletionChnage = (e: { target: { value: any; }; }) => {
        const val = e.target.value;
        if (val > 100) {
            setCompletion(100)
        } else if (val < 0) {
            setCompletion(0)
        } else {
            setCompletion(val)
        }

    }
    const handleDurationChange = (e: { target: { value: any; }; }) => {
        const val = e.target.value;
        if (val < 0) {
            setDuration(0)
        } else {
            setDuration(val)
        }

    }

    const handleDelayedDurationChange = (e: { target: { value: any; }; }) => {
        const val = e.target.value;
        if (val < 0) {
            setDelayedDuration(0)
        } else {
            setDelayedDuration(val)
        }
    }

    const tasksOption = tasks.slice(1).filter(({ id, item_id, }) => {
        const ids = id || item_id;
        const _ids = task?.id || task?.item_id;
        return ids !== _ids;
    }).map(({ name, id, item_id, task_title }) => {
        const ids = id || item_id;
        const _name = name || task_title
        return {
            value: ids,
            label: `${ids} : ${_name}`
        };
    })


    return (<>
        <Drawer
            isOpen={isVisiable && !showDeleteConfirm}
            onClose={handleCloseModal}
            position="right"
        >
            <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
                <div className={`flex flex-row border-b border-gray-200 !p-6 dark:border-gray-700 bg-white dark:bg-[#111928] sticky top-0 z-40`}>
                    <div className="flex w-full h-12 items-center  ">
                        {task.item_id ? <div className="w-12 h-6 bg-orange-400 rounded-2xl p-2 mr-4" >
                            <div className="text-center text-white text-xs font-normal font-['Inter'] leading-3">Edit</div>
                        </div> :
                            !taskLoading ? <div className="w-12 h-6 bg-green-400 rounded-2xl p-2 mr-4" >
                                <div className="text-center text-white text-xs font-normal font-['Inter'] leading-3">New</div>
                            </div> : ""}
                        {task.item_id && !taskLoading && <HiCode className="text-3xl border mr-2" />}
                        <div className="flex flex-col justify-center w-full">
                            {task.item_id && <div className="opacity-50 dark:text-white text-gray-700 text-xs font-bold font-['Inter'] uppercase leading-none mr-2">{task.item_id}   |   {task.duration} days</div>}
                            {!taskLoading && <div className={`dark:text-white text-gray-700 ${task.item_id ? "text-sm" : "text-xl"} font-normal font-['Inter'] leading-tight`} title={task.task_title ? task.task_title : "Create Task"}>{task.task_title ? task.task_title : "Create Task"}</div>}
                            {task.item_id && (
                                <div className="dark:text-white text-gray-500 text-xs">
                                    {task.created_by ? (
                                        <>Created by: {getDefaultUserName(task.created_by) || getResourceName(task.created_by)} on {moment(task.created_at).format('ll')}</>
                                    ) : task.created_at ? (
                                        <>Created on: {moment(task.created_at).format('ll')}</>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="dark:text-white text-gray-700 text-base font-normal truncate overflow-hidden w-72" title={task.name}>{task.name}</div>
                        {task.item_id && !taskLoading && <HiTrash className="text-3xl text-red-800 cursor-pointer mr-2" onClick={() => handleDeleteTask(true)} />}
                        <button type="button" className="ml-auto absolute right-[10px] top-0 dark:text-white text-gray-700 w-6 h-6 rounded-full bg-black text-sm" onClick={handleCloseModal}>
                            X
                        </button>
                    </div>
                </div>
                <div className='px-6 xs:px-10 py-5'>
                    {taskLoading ? (
                        <div className="text-center w-full h-full bottom-0">
                            <Spinner aria-label="Center-aligned spinner example" />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-5">
                            <FormInput
                                id="title"
                                name="title"
                                label="Title"
                                type="text"
                                value={task_title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Title"
                                required
                                showAsterisk
                            />

                            <div>
                                <Label className="text-sm font-normal font-['Inter'] leading-tight mb-2 text-gray-900 dark:text-white" htmlFor="description">
                                    Description *
                                </Label>
                                <div className="mt-1">
                                    <RichTextEditor
                                        content={description || ''}
                                        onChange={setDescription}
                                        placeholder="Enter task description..."
                                        disabled={false}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-5">
                                <CustomDropdown
                                    id="skill"
                                    name="skill"
                                    label="Skill Required"
                                    value={skill?.toString()}
                                    onChange={(value) => {
                                        setSkill(value);
                                        setResponsible('');
                                    }}
                                    options={skills.map((s: any) => ({
                                        value: s.id.toString(),
                                        label: s.skill
                                    }))}
                                    placeholder="Choose a skill name"
                                    required
                                    showAsterisk
                                    disabled={skillLoading}
                                />

                                <div>
                                    <CustomDropdown
                                        id="TaskType"
                                        name="task_type"
                                        label={
                                            <span className="flex items-center">
                                                Task Type
                                                <Tooltip placement="top" content="Task type are assigned at create. Only task type 'Tasks' shows up in Gantt view.">
                                                    <HiQuestionMarkCircle className="text-xl ml-1" />
                                                </Tooltip>
                                            </span>
                                        }
                                        value={task_type}
                                        onChange={(value) => setTask_type(value)}
                                        options={[
                                            { value: 'task', label: 'Task' },
                                            { value: 'float', label: 'Float' }
                                        ]}
                                        placeholder="Choose a Task Type"
                                        disabled={true}
                                    />
                                </div>
                            </div>

                            {task_type !== 'float' && (
                                <div className="w-full">
                                    <MultiSelect
                                        id="dependencies"
                                        name="dependencies"
                                        label="Dependent On"
                                        value={tasksOption.filter((ele) => dependenciesIds.includes(ele.value))}
                                        onChange={(selectedOptions) => {
                                            handleSelectChange(selectedOptions);
                                        }}
                                        options={tasksOption}
                                        placeholder="Select dependencies"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <NumberInput
                                        id="duration"
                                        name="duration"
                                        label="Duration (In Days)"
                                        value={duration}
                                        onChange={handleDurationChange}
                                        placeholder="Duration"
                                        min={1}
                                        required
                                        showAsterisk
                                    />
                                </div>
                                {task.item_id && (
                                    <div>
                                        <NumberInput
                                            id="delayed_duration"
                                            name="delayed_duration"
                                            label="Delay Duration (In Days)"
                                            value={delayed_duration}
                                            onChange={handleDelayedDurationChange}
                                            placeholder="Delay Duration"
                                            min={0}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {!isToken && (
                    <div className="px-6 py-2 items-center flex gap-5 mt-auto border-t border-gray-200 dark:border-gray-700 fixed bottom-0 right-0 left-0">
                        <CustomButton onClick={handleCloseModal} variant="cancel">
                            Cancel
                        </CustomButton>
                        <CustomButton
                            onClick={() => onSubmit()}
                            className="ml-auto"
                            disabled={task?.item_id && !unsavedChanges}
                        >
                            {task?.item_id ? 'Update' : 'Create'}
                        </CustomButton>
                    </div>
                )}
            </div>
        </Drawer>
        <DeleteModal showModal={showDeleteConfirm} setShowModal={handleDeleteTask} handleConfirm={submitDeleteTask} />
    </>);
}

export default TaskCompletionUpdateModal;